/**
 * Cart Alert - Handle products made in China
 * Disables checkout button for US customers with Chinese products until they accept tariff terms
 * 
 * This script works in two parts:
 * 1. When viewing product pages, it detects Chinese products and marks them in localStorage
 * 2. When viewing cart, it checks if any Chinese products are in cart and disables checkout
 */
(() => {
  // State tracking to prevent loops
  let isUpdating = false;
  let lastUpdateTime = 0;
  let debugMode = true; // Set to true to enable console logs for debugging
  const TARIFF_CHECKBOX_ID = 'china-tariff-agreement';
  const WARNING_BOX_ID = 'china-tariff-warning';
  
  // Log function that only works in debug mode
  const log = (...args) => {
    if (debugMode) console.log('[Cart Alert]', ...args);
  };
  
  // Debounce function to prevent too many calls
  const debounce = (func, wait) => {
    let timeout;
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout);
        func(...args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  };
  
  // Store to keep track of Chinese products
  const CHINA_PRODUCTS_KEY = 'china_products';
  
  // Check if user is in the US
  const isUSCustomer = () => {
    const userCountry = sessionStorage.getItem('country') || window.customerCountry;
    log('Customer country:', userCountry);
    return userCountry === 'US';
  };
  
  // Save a product ID to our list of Chinese products
  const markProductAsChinese = (productId) => {
    try {
      let chinaProducts = JSON.parse(localStorage.getItem(CHINA_PRODUCTS_KEY) || '[]');
      if (!chinaProducts.includes(productId)) {
        chinaProducts.push(productId);
        localStorage.setItem(CHINA_PRODUCTS_KEY, JSON.stringify(chinaProducts));
      }
    } catch (e) {
      console.error('Error storing Chinese product:', e);
    }
  };
  
  // Check if a product ID is in our list of Chinese products
  const isMarkedAsChinese = (productId) => {
    try {
      const chinaProducts = JSON.parse(localStorage.getItem(CHINA_PRODUCTS_KEY) || '[]');
      return chinaProducts.includes(productId);
    } catch (e) {
      console.error('Error checking Chinese product:', e);
      return false;
    }
  };
  
  // Detect if current product page shows a Chinese product
  const detectChineseProductOnPage = () => {
    // Method 1: Check for visible tariff alerts on the product page
    const tariffAlerts = document.querySelectorAll('.tariff_alert');
    if (tariffAlerts.length > 0) {
      // Get the product ID from the page
      const productForm = document.querySelector('form[action="/cart/add"]');
      if (productForm) {
        const productIdInput = productForm.querySelector('input[name="id"]');
        if (productIdInput) {
          const variantId = productIdInput.value;
          const productId = window.meta?.product?.id || 
                            document.querySelector('div[data-product-id]')?.dataset.productId;
          
          if (productId) {
            console.log('Detected Chinese product:', productId);
            markProductAsChinese(productId);
          }
        }
      }
    }
    
    // Method 2: Check for China metafield in product JSON
    try {
      if (window.meta && window.meta.product) {
        const product = window.meta.product;
        if ((product.metafields && product.metafields.custom && product.metafields.custom.country === 'China') ||
            document.querySelector('[data-country="China"]')) {
          markProductAsChinese(product.id);
        }
      }
    } catch (e) {
      console.error('Error checking product metafields:', e);
    }
  };
  
  // Detect Chinese products ONLY in the cart (not elsewhere on the page)
  const hasChineseProductsInCart = () => {
    // First, find all cart containers to limit our search scope
    const cartContainers = document.querySelectorAll(
      '.cart, .cart__drawer, .cart-drawer, .mini-cart, ' + 
      '[data-section-type="cart"], [data-section-id="cart"], ' +
      '[data-section-type="cart-template"], .cart-form, ' +
      '[data-cart-wrapper], [data-cart-contents]'
    );
    
    log('Found cart containers:', cartContainers.length);
    
    // If no cart containers found, assume no Chinese products
    if (cartContainers.length === 0) {
      log('No cart containers found, assuming no Chinese products');
      return false;
    }
    
    // Check each cart container for Chinese products
    for (const container of cartContainers) {
      // Look for tariff alerts within the cart container
      const tariffAlerts = container.querySelectorAll('.tariff_alert:not([style*="display: none"])');
      if (tariffAlerts.length > 0) {
        log('Found visible tariff alert in cart:', tariffAlerts.length);
        return true;
      }
      
      // Look for cart items with China indicators
      const cartItems = container.querySelectorAll('.cart__item, .cart-item, .line-item');
      log('Found cart items:', cartItems.length);
      
      for (const item of cartItems) {
        // Check data attributes
        if (item.dataset.country === 'China' || 
            item.hasAttribute('data-product-country') && item.getAttribute('data-product-country') === 'China') {
          log('Found cart item with China data attribute');
          return true;
        }
        
        // Check for other China indicators within the cart item only
        const chinaElements = item.querySelectorAll('[data-country="China"]');
        if (chinaElements.length > 0) {
          log('Found element with China data attribute in cart item');
          return true;
        }
        
        // Check product title or description
        const productTitle = item.querySelector('.cart-item__name, .cart__item-title, .cart-item-title');
        if (productTitle && productTitle.textContent.includes('China')) {
          log('Found China in product title');
          return true;
        }
        
        // Very specific text match to avoid false positives
        if (item.textContent.includes('Made in China') || 
            item.textContent.includes('Origin: China')) {
          log('Found China text in cart item');
          return true;
        }
      }
    }
    
    log('No Chinese products found in cart');
    return false;
  };
  
  // Check if the tariff agreement checkbox is checked
  const isTariffAgreementChecked = () => {
    const checkbox = document.getElementById(TARIFF_CHECKBOX_ID);
    return checkbox && checkbox.checked;
  };
  
  // Create tariff warning box with checkbox
  const createTariffWarningBox = (button) => {
    if (!button) {
      log('No button provided to createTariffWarningBox');
      return null;
    }
    
    // First remove any existing warning box
    removeTariffWarningBox();
    
    log('Creating tariff warning box for button:', button);
    
    // Find a suitable container
    const formContainer = button.closest('form');
    const buttonContainer = button.parentNode;
    const cartFooter = document.querySelector('.cart__footer, .cart-footer');
    const container = formContainer || buttonContainer || cartFooter;
    
    if (!container) {
      log('No suitable container found for warning box');
      return null;
    }
    
    // Create warning container with inline styles for maximum compatibility
    const warningBox = document.createElement('div');
    warningBox.id = WARNING_BOX_ID;
    warningBox.className = 'cart__warning tariff-agreement';
    warningBox.setAttribute('style', 
      'color: #c00; ' +
      'margin: 20px 0; ' + 
      'border: 2px solid #c00; ' + 
      'padding: 15px; ' + 
      'text-align: left; ' +
      'background-color: rgba(227, 44, 43, 0.05); ' +
      'max-width: 100%; ' +
      'width: 100%; ' +
      'box-sizing: border-box; ' +
      'z-index: 9999; ' +
      'position: relative;'
    );
    
    // Create header
    const header = document.createElement('p');
    header.setAttribute('style', 
      'font-weight: bold; ' +
      'margin-bottom: 10px; ' +
      'margin-top: 0; ' +
      'font-size: 14px;'
    );
    header.textContent = 'Your cart contains products made in China which may be subject to U.S. import tariffs.';
    
    // Create checkbox container
    const checkboxWrapper = document.createElement('label');
    checkboxWrapper.setAttribute('style', 
      'display: flex; ' +
      'align-items: flex-start; ' +
      'cursor: pointer; ' +
      'user-select: none; ' +
      'font-size: 14px;'
    );
    
    // Create checkbox
    const checkbox = document.createElement('input');
    checkbox.type = 'checkbox';
    checkbox.id = TARIFF_CHECKBOX_ID;
    checkbox.setAttribute('style', 
      'margin-right: 10px; ' +
      'margin-top: 3px;'
    );
    
    // Create label text
    const label = document.createElement('span');
    label.setAttribute('style', 'flex: 1;');
    label.textContent = "I understand that my order includes products made in China and may be subject to US import tariffs. I agree that any additional fees charged by the shipping carrier at or after my order is delivered are my responsibility.";
    
    // Assemble elements
    checkboxWrapper.appendChild(checkbox);
    checkboxWrapper.appendChild(label);
    warningBox.appendChild(header);
    warningBox.appendChild(checkboxWrapper);
    
    // Always insert directly before the checkout button
    button.parentNode.insertBefore(warningBox, button);
    
    log('Warning box created and inserted above checkout button');
    
    // Add event listener to checkbox
    checkbox.addEventListener('change', () => {
      log('Checkbox changed:', checkbox.checked);
      forceUpdateCheckoutButtons();
    });
    
    return warningBox;
  };
  
  // Force remove existing warning box
  const removeTariffWarningBox = () => {
    const existingWarning = document.getElementById(WARNING_BOX_ID);
    if (existingWarning) {
      log('Removing existing warning box');
      existingWarning.parentNode.removeChild(existingWarning);
    }
  };
  
  // Force update checkout buttons immediately
  const forceUpdateCheckoutButtons = () => {
    // Find checkout buttons
    const checkoutButtons = document.querySelectorAll(
      '.cart__checkout, ' +
      '[name="checkout"], ' + 
      'button[type="submit"][form="cart"], ' + 
      '.cart__submit, ' + 
      '.cart-checkout, ' + 
      '.checkout-button, ' + 
      '#checkout'
    );
    
    log('Force updating checkout buttons:', checkoutButtons.length);
    
    const agreementCheckbox = document.getElementById(TARIFF_CHECKBOX_ID);
    const isAgreed = agreementCheckbox && agreementCheckbox.checked;
    
    for (const button of checkoutButtons) {
      // Check current state to avoid unnecessary DOM operations
      const isCurrentlyDisabled = button.disabled || button.hasAttribute('disabled');
      const shouldBeDisabled = !isAgreed;
      
      // Only update if state needs to change
      if (shouldBeDisabled !== isCurrentlyDisabled) {
        log(`Changing button state from ${isCurrentlyDisabled ? 'disabled' : 'enabled'} to ${shouldBeDisabled ? 'disabled' : 'enabled'}`);
        
        if (shouldBeDisabled) {
          // Disable
          button.disabled = true;
          button.setAttribute('disabled', 'disabled');
          button.classList.add('btn--disabled');
          button.style.opacity = '0.5';
          button.style.cursor = 'not-allowed';
          button.style.pointerEvents = 'none';
          button.setAttribute('data-disabled-by-tariff', 'true');
        } else {
          // Enable
          button.disabled = false;
          button.removeAttribute('disabled');
          button.classList.remove('btn--disabled');
          button.removeAttribute('data-disabled-by-tariff');
          button.style.opacity = '';
          button.style.cursor = '';
          button.style.pointerEvents = '';
        }
        
        log(`Button state updated to ${shouldBeDisabled ? 'disabled' : 'enabled'}`);
      }
    }
  };
  
  // Force enable all checkout buttons
  const forceEnableCheckoutButtons = () => {
    const checkoutButtons = document.querySelectorAll(
      '.cart__checkout, ' +
      '[name="checkout"], ' + 
      'button[type="submit"][form="cart"], ' + 
      '.cart__submit, ' + 
      '.cart-checkout, ' + 
      '.checkout-button, ' + 
      '#checkout'
    );
    
    log('Force ENABLING ALL checkout buttons:', checkoutButtons.length);
    
    for (const button of checkoutButtons) {
      // Only update if button is currently disabled
      if (button.disabled || button.hasAttribute('disabled') || button.classList.contains('btn--disabled')) {
        // Enable
        button.disabled = false;
        button.removeAttribute('disabled');
        button.classList.remove('btn--disabled');
        button.removeAttribute('data-disabled-by-tariff');
        button.style.opacity = '';
        button.style.cursor = '';
        button.style.pointerEvents = '';
        
        log('Enabled checkout button that was disabled');
      }
    }
  };
  
  // Main function to check cart and update UI
  const updateCheckoutButtons = (trigger = 'manual') => {
    // Prevent multiple simultaneous updates
    const now = Date.now();
    if (isUpdating || (now - lastUpdateTime < 150)) {
      return;
    }
    
    try {
      isUpdating = true;
      lastUpdateTime = now;
      log('Updating checkout buttons. Trigger:', trigger);
      
      // Only proceed if customer is in the US
      if (!isUSCustomer()) {
        log('Not a US customer, skipping checks');
        return;
      }
      
      // Check cart containers for Chinese products
      const hasChineseProducts = hasChineseProductsInCart();
      log('Has Chinese products in cart:', hasChineseProducts);
      
      // Find checkout buttons
      const checkoutButtons = document.querySelectorAll(
        '.cart__checkout, ' +
        '[name="checkout"], ' + 
        'button[type="submit"][form="cart"], ' + 
        '.cart__submit, ' + 
        '.cart-checkout, ' + 
        '.checkout-button, ' + 
        '#checkout'
      );
      
      log('Found checkout buttons:', checkoutButtons.length);
      
      // If we have Chinese products and checkout buttons
      if (hasChineseProducts && checkoutButtons.length > 0) {
        // Find or create tariff warning box
        const warningBox = document.getElementById(WARNING_BOX_ID);
        if (!warningBox) {
          log('No warning box found, creating one');
          createTariffWarningBox(checkoutButtons[0]);
        }
        
        // Update button states
        forceUpdateCheckoutButtons();
      } else if (!hasChineseProducts) {
        // No Chinese products, remove warning and enable buttons
        removeTariffWarningBox();
        
        // Force enable ALL checkout buttons
        forceEnableCheckoutButtons();
        
        log('No Chinese products - all checkout buttons should be enabled');
      }
    } finally {
      // Always reset updating flag
      setTimeout(() => {
        isUpdating = false;
      }, 100);
    }
  };
  
  // Force recheck and update after cart fetch completes (used for Ajax updates)
  const setupFetchInterceptor = () => {
    const originalFetch = window.fetch;
    window.fetch = function(url, options) {
      return originalFetch.apply(this, arguments).then(response => {
        if (url && typeof url === 'string' && 
            (url.includes('/cart') || url.includes('/cart.js') || url.includes('cart/update') || url.includes('cart/add'))) {
          // This is a cart-related fetch, schedule a recheck
          log('Intercepted cart fetch:', url);
          setTimeout(() => updateCheckoutButtons('fetch-intercept'), 300);
          setTimeout(() => updateCheckoutButtons('fetch-intercept-delayed'), 800);
        }
        return response;
      });
    };
  };
  
  // Setup jquery ajax interceptor if jQuery is available
  const setupAjaxInterceptor = () => {
    if (window.jQuery) {
      log('Setting up jQuery ajax interceptor');
      jQuery(document).ajaxComplete(function(event, xhr, settings) {
        if (settings.url && 
            (settings.url.includes('/cart') || settings.url.includes('cart/update') || settings.url.includes('cart/add'))) {
          log('Intercepted jQuery ajax call:', settings.url);
          setTimeout(() => updateCheckoutButtons('ajax-intercept'), 300);
        }
      });
    }
  };
  
  // Force a tariff check now for debugging purposes
  const debugCheckTariffs = () => {
    log('*** FORCED TARIFF CHECK ***');
    
    // First check if there are any Chinese products
    const hasChineseProducts = hasChineseProductsInCart();
    log('Debug check - Has Chinese products in cart:', hasChineseProducts);
    
    if (!hasChineseProducts) {
      // If no Chinese products, force enable all checkout buttons
      log('No Chinese products detected - forcing enable of all checkout buttons');
      removeTariffWarningBox();
      forceEnableCheckoutButtons();
    } else {
      // If Chinese products found, update normally
      updateCheckoutButtons('debug-force');
    }
    
    // Run another check after a delay
    setTimeout(() => updateCheckoutButtons('debug-force-delayed'), 1000);
  };
  
  // Initialize and setup event listeners
  const init = () => {
    log('Initializing cart-alert.js');
    
    // Create debounced update function
    const debouncedUpdate = debounce(updateCheckoutButtons, 250);
    
    // Setup XHR/Fetch interceptors
    setupFetchInterceptor();
    setupAjaxInterceptor();
    
    // Run initial check
    setTimeout(() => updateCheckoutButtons('init'), 100);
    
    // Listen for cart update events
    const cartEvents = [
      'theme:cart:added',
      'theme:cart:update',
      'theme:cart:refresh',
      'cart:refresh',
      'cart:updated',
      'cart:change',
      'change'
    ];
    
    cartEvents.forEach(event => {
      document.addEventListener(event, () => {
        log('Cart event detected:', event);
        debouncedUpdate(event);
        // Double-check after a slight delay
        setTimeout(() => updateCheckoutButtons(`delayed-${event}`), 500);
      });
    });
    
    // Set up mutation observer for dynamic cart changes
    const cartContainers = document.querySelectorAll(
      '.cart, .cart__drawer, .cart-drawer, .mini-cart, ' + 
      '[data-section-type="cart"], [data-section-id="cart"], ' +
      '[data-section-type="cart-template"], .cart-form, ' +
      '[data-cart-wrapper], [data-cart-contents]'
    );
    
    if (cartContainers.length > 0) {
      log('Setting up mutation observer on cart containers:', cartContainers.length);
      
      const observer = new MutationObserver((mutations) => {
        let shouldUpdate = false;
        
        for (const mutation of mutations) {
          // Skip our own changes
          if (mutation.target.id === WARNING_BOX_ID || 
              mutation.target.id === TARIFF_CHECKBOX_ID ||
              (mutation.target.hasAttribute && mutation.target.hasAttribute('data-china-warning')) ||
              (mutation.target.hasAttribute && mutation.target.hasAttribute('data-disabled-by-tariff'))) {
            continue;
          }
          
          // Also skip changes to disabled attribute on buttons we've already handled
          if (mutation.type === 'attributes' && 
              mutation.attributeName === 'disabled' && 
              mutation.target.hasAttribute('data-disabled-by-tariff')) {
            continue;
          }
          
          // Look for cart changes
          if (mutation.type === 'childList' || 
              (mutation.type === 'attributes' && 
               mutation.target.classList)) {
            shouldUpdate = true;
            break;
          }
        }
        
        if (shouldUpdate) {
          log('Cart DOM changed, updating');
          debouncedUpdate('mutation');
        }
      });
      
      cartContainers.forEach(container => {
        observer.observe(container, { 
          childList: true,
          subtree: true,
          attributes: true
        });
      });
    }
    
    // Add a periodic check every 5 seconds instead of 2 for less CPU usage
    setInterval(() => updateCheckoutButtons('interval'), 5000);
    
    // Force tariff check immediately
    debugCheckTariffs();
  };
  
  // Run when DOM is ready
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
  
  // Also run when window is fully loaded (with a slight delay)
  window.addEventListener('load', () => {
    log('Window loaded, running tariff check');
    setTimeout(() => updateCheckoutButtons('window-load'), 300);
    setTimeout(debugCheckTariffs, 1000);
  });
})(); 